import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { SubtaskFormSchema } from '../schemas';

// Extraído de `ComponentTaskHoy.jsx` (02.1-b6-taskhoy-orchestrator.md, paso D3) -- estado y
// lógica del wizard de creación de subtareas (el JSX presentacional ya vivía en
// `SubtaskCreateWizard.jsx` desde la Fase A). El wizard de 5 pasos vive sobre un único `<input>`
// que cambia de significado por paso (título/descripción/encargado/duración/supervisor); lo que
// se escribe en pantalla en los pasos de encargado/supervisor es un texto de búsqueda para el
// autocomplete, no el valor final del campo (ese se fija al elegir un nick) -- por eso
// `newSubtaskTitle`/`subtaskFilteredUsers`/`subtaskSelectedUserIndex` siguen siendo estado de UI
// transitorio, no data del form. `subtaskForm` (react-hook-form + `zodResolver`,
// `SubtaskFormSchema`) reemplaza el objeto `subtaskFormData` a mano.
export function useTaskSubtaskWizard({ activeTask, systemUsers, user, activeSpaceId, createTaskMutation }) {
    const [newSubtaskTitle, setNewSubtaskTitle] = useState('');
    const [subtaskInputState, setSubtaskInputState] = useState(0); // 0: title, 1: desc, 2: assignee
    const [subtaskFilteredUsers, setSubtaskFilteredUsers] = useState([]);
    const [subtaskSelectedUserIndex, setSubtaskSelectedUserIndex] = useState(0);
    const subtaskForm = useForm({
        resolver: zodResolver(SubtaskFormSchema),
        defaultValues: { title: '', description: '', assignee: null, duration: 30, supervisor: null },
    });

    // `subtaskForm.handleSubmit` valida contra `SubtaskFormSchema` antes de llamar acá -- `data`
    // ya viene con los defaults aplicados (`description`/`duration`/etc.).
    const onCreateSubtask = async (data) => {
        setSubtaskInputState(0);
        setNewSubtaskTitle('');
        subtaskForm.reset();

        try {
            // La subtarea nace ya enlazada a su padre vía `parent_task_id`.
            await createTaskMutation.mutateAsync({
                title: data.title,
                description: data.description,
                type: 'task',
                status: 'open',
                stage: 'planeada',
                autoSchedule: true,
                tiempo: `${data.duration} min`,
                duration_minutes: data.duration,
                assignee: data.assignee,
                supervisor: data.supervisor,
                actorId: user ? user.id : null,
                space_id: activeSpaceId || null,
                parent_task_id: activeTask.id,
            });
            // `useCreateTask` (`onSuccess`) + el socket `task_created` refrescan la lista.
        } catch (err) {
            console.error("Error creating subtask", err);
        }
    };

    // El paso final: `assignee`/`duration` ya están en `subtaskForm` (pasos 2 y 3); sólo falta
    // `supervisor` para disparar el submit real de RHF.
    const handleCreateSubtask = (supervisorNick) => {
        subtaskForm.setValue('supervisor', supervisorNick);
        subtaskForm.handleSubmit(onCreateSubtask)();
    };

    const handleSubtaskKeyDown = async (e) => {
        if (e.key === 'Escape') {
            setSubtaskInputState(0);
            subtaskForm.reset();
            setNewSubtaskTitle('');
            return;
        }

        if (subtaskInputState === 2) {
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                setSubtaskSelectedUserIndex(prev => Math.min(prev + 1, Math.min(subtaskFilteredUsers.length - 1, 2)));
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setSubtaskSelectedUserIndex(prev => Math.max(prev - 1, 0));
            } else if (e.key === 'Enter') {
                e.preventDefault();
                let assigneeNick = null;
                if (subtaskFilteredUsers.length > 0 && newSubtaskTitle.trim() !== '') {
                    assigneeNick = subtaskFilteredUsers[subtaskSelectedUserIndex].nick;
                }
                subtaskForm.setValue('assignee', assigneeNick);
                setSubtaskInputState(3);
                setNewSubtaskTitle('');
            }
        } else if (subtaskInputState === 3) {
            if (e.key === 'Enter') {
                e.preventDefault();
                const durationMinutes = parseInt(newSubtaskTitle.trim()) || 30;
                subtaskForm.setValue('duration', durationMinutes);
                setSubtaskInputState(4);
                setNewSubtaskTitle('');
                setSubtaskFilteredUsers(systemUsers);
                setSubtaskSelectedUserIndex(0);
            }
        } else if (subtaskInputState === 4) {
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                setSubtaskSelectedUserIndex(prev => Math.min(prev + 1, Math.min(subtaskFilteredUsers.length - 1, 2)));
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setSubtaskSelectedUserIndex(prev => Math.max(prev - 1, 0));
            } else if (e.key === 'Enter') {
                e.preventDefault();
                let supervisorNick = null;
                if (subtaskFilteredUsers.length > 0 && newSubtaskTitle.trim() !== '') {
                    supervisorNick = subtaskFilteredUsers[subtaskSelectedUserIndex].nick;
                }
                handleCreateSubtask(supervisorNick);
            }
        } else {
            if (e.key === 'Enter') {
                e.preventDefault();
                const val = newSubtaskTitle.trim();
                if (subtaskInputState === 0) {
                    subtaskForm.setValue('title', val);
                    // `trigger` corre `SubtaskFormSchema` sólo sobre `title` -- si está vacío,
                    // `subtaskForm.formState.errors.title` se llena y el wizard no avanza (antes:
                    // `if (!val) return`, sin mensaje visible).
                    const valid = await subtaskForm.trigger('title');
                    if (!valid) return;
                    setSubtaskInputState(1);
                    setNewSubtaskTitle('');
                } else if (subtaskInputState === 1) {
                    subtaskForm.setValue('description', val);
                    setSubtaskInputState(2);
                    setNewSubtaskTitle('');
                    setSubtaskFilteredUsers(systemUsers);
                    setSubtaskSelectedUserIndex(0);
                }
            }
        }
    };

    return {
        subtaskForm, newSubtaskTitle, setNewSubtaskTitle,
        subtaskInputState, setSubtaskInputState,
        subtaskFilteredUsers, setSubtaskFilteredUsers,
        subtaskSelectedUserIndex, setSubtaskSelectedUserIndex,
        handleCreateSubtask, handleSubtaskKeyDown,
    };
}
